/**
 * fc-encryption/crypto/aes-gcm.ts
 *
 * AES-256-GCM authenticated encryption via Web Crypto API.
 *
 * Two encrypt/decrypt pairs:
 * - `encrypt(plaintext, passphrase)` / `decrypt(...)`: takes a passphrase + runs KDF.
 *   Used by the verifier (first-launch passphrase set, subsequent unlock verification).
 *   Slow by design — KDF is intentional cost against brute-force.
 * - `encryptWithKey(plaintext, keyBytes)` / `decryptWithKey(...)`: takes 32 raw key
 *   bytes directly (from session-key.ts). Skips KDF entirely. This is the hot path.
 *
 * Output format: { ciphertext, iv, salt }
 * - ciphertext: base64 of (ciphertext + 16-byte auth tag)
 * - iv: base64 of 12-byte IV
 * - salt: base64 of 16-byte salt (informational; passed back to decrypt for symmetry)
 */

import { arrayBufferToBase64, base64ToArrayBuffer, generateIV, generateSalt } from './salts';
import { deriveKey, deriveKeyWithId, deriveKeyWithKdf, LEGACY_DEFAULT_KDF, type KdfId } from './kdf';

const KEY_BITS = 256;
const ALGORITHM = 'AES-GCM' as const;

/** Test/instrumentation hook: counts KDF invocations from the hot path. */
let kdfCallCount = 0;

/** Reset the KDF counter (test-only). */
export function resetKdfCallCount(): void {
  kdfCallCount = 0;
}

/** Read the KDF counter (test-only). */
export function getKdfCallCount(): number {
  return kdfCallCount;
}

/**
 * Encrypt a plaintext string with the given passphrase.
 * Runs KDF (Argon2id or PBKDF2 fallback) — slow by design.
 * Use `encryptWithKey` on the hot path to avoid KDF.
 *
 * @param plaintext - UTF-8 string to encrypt
 * @param passphrase - the user's passphrase
 * @returns { ciphertext, iv, salt, algorithm: 'AES-256-GCM' }
 */
export async function encrypt(plaintext: string, passphrase: string): Promise<{
  ciphertext: string;
  iv: string;
  salt: string;
  algorithm: typeof ALGORITHM;
  /** F 145 D — the key schedule that actually produced `ciphertext`. */
  kdf: KdfId;
}> {
  const salt = generateSalt();
  const iv = generateIV();
  kdfCallCount++;
  // F 145 D — deriveKeyWithId, not deriveKey, so the record can name the key
  // schedule. This is the ONLY path that derives from a passphrase, so it is
  // the only place a `kdf` field is honest.
  const { key: keyB64, kdf } = await deriveKeyWithId(passphrase, salt);
  const keyBuf = base64ToArrayBuffer(keyB64);
  const ivBuf = base64ToArrayBuffer(iv);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBuf,
    { name: ALGORITHM },
    false,
    ['encrypt']
  );

  const enc = new TextEncoder();
  const ctBuf = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: ivBuf },
    cryptoKey,
    enc.encode(plaintext)
  );

  return {
    ciphertext: arrayBufferToBase64(ctBuf),
    iv,
    salt,
    algorithm: ALGORITHM,
    kdf,
  };
}

/**
 * Decrypt ciphertext with the given passphrase.
 * Runs KDF — slow by design. Use `decryptWithKey` on the hot path.
 *
 * Throws on:
 * - Wrong passphrase (auth tag mismatch → `DecryptionError`)
 * - Tampered ciphertext
 * - Malformed base64
 */
export async function decrypt(
  ciphertext: string,
  iv: string,
  salt: string,
  passphrase: string,
  /**
   * F 145 D — the KDF the record says sealed it. Omit for a record written
   * before F 145 D, and the other KDF is tried on an auth failure.
   */
  recorded?: KdfId,
): Promise<string> {
  // F 145 D — honour what the record says. `deriveKey`'s automatic preference
  // is exactly the bug: a record sealed with PBKDF2 on a platform where argon2
  // loads would be read with argon2id, produce a different key, and fail.
  //
  // For a legacy record the KDF is unknown, so both are tried. That is a probe
  // rather than a guess: a wrong KDF yields a wrong key and AES-GCM rejects it,
  // so the wrong attempt cannot masquerade as success.
  const kdf: KdfId = recorded ?? LEGACY_DEFAULT_KDF;
  const attempt = async (k: KdfId) => {
    const keyB64 = await deriveKeyWithKdf(passphrase, salt, k);
    const cryptoKey = await crypto.subtle.importKey(
      'raw', base64ToArrayBuffer(keyB64), { name: ALGORITHM }, false, ['decrypt']
    );
    const pt = await crypto.subtle.decrypt(
      { name: ALGORITHM, iv: base64ToArrayBuffer(iv) },
      cryptoKey,
      base64ToArrayBuffer(ciphertext)
    );
    return new TextDecoder().decode(pt);
  };

  try {
    return await attempt(kdf);
  } catch (primaryError) {
    if (recorded) throw primaryError;   // it SAID; a failure is real
    const alternate: KdfId = kdf === 'argon2id' ? 'pbkdf2' : 'argon2id';
    try {
      return await attempt(alternate);
    } catch {
      // Neither worked — report the ORIGINAL error, which is the accurate one.
      throw primaryError;
    }
  }


}

/**
 * Encrypt with a pre-derived 256-bit key. NO KDF call.
 * This is the hot path used by api/encrypt-local.ts.
 *
 * The salt parameter is still generated per-block (so each EncryptedBlock carries
 * its own salt for the record), but is not used for key derivation — the key is
 * already in hand. The salt is informational only.
 *
 * @param plaintext - UTF-8 string to encrypt
 * @param keyBytes - 32 raw key bytes (from session-key.ts)
 * @returns { ciphertext, iv, salt, algorithm: 'AES-256-GCM' }
 */
export async function encryptWithKey(
  plaintext: string,
  keyBytes: Uint8Array
): Promise<{
  ciphertext: string;
  iv: string;
  salt: string;
  algorithm: typeof ALGORITHM;
}> {
  if (keyBytes.byteLength !== KEY_BITS / 8) {
    throw new Error(`encryptWithKey: key must be 32 bytes (got ${keyBytes.byteLength})`);
  }
  const salt = generateSalt();
  const iv = generateIV();
  const ivBuf = base64ToArrayBuffer(iv);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBytes as BufferSource,
    { name: ALGORITHM },
    false,
    ['encrypt']
  );

  const enc = new TextEncoder();
  const ctBuf = await crypto.subtle.encrypt(
    { name: ALGORITHM, iv: ivBuf },
    cryptoKey,
    enc.encode(plaintext)
  );

  return {
    ciphertext: arrayBufferToBase64(ctBuf),
    iv,
    salt,
    algorithm: ALGORITHM,
  };
}

/**
 * Decrypt with a pre-derived 256-bit key. NO KDF call.
 * Hot path used by api/decrypt-local.ts.
 *
 * Throws DecryptionError on:
 * - Wrong key (auth tag mismatch)
 * - Tampered ciphertext
 * - Malformed base64
 */
export async function decryptWithKey(
  ciphertext: string,
  iv: string,
  keyBytes: Uint8Array
): Promise<string> {
  if (keyBytes.byteLength !== KEY_BITS / 8) {
    throw new Error(`decryptWithKey: key must be 32 bytes (got ${keyBytes.byteLength})`);
  }
  const ivBuf = base64ToArrayBuffer(iv);
  const ctBuf = base64ToArrayBuffer(ciphertext);

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyBytes as BufferSource,
    { name: ALGORITHM },
    false,
    ['decrypt']
  );

  let ptBuf: ArrayBuffer;
  try {
    ptBuf = await crypto.subtle.decrypt(
      { name: ALGORITHM, iv: ivBuf },
      cryptoKey,
      ctBuf
    );
  } catch (e) {
    throw new DecryptionError('Decryption failed (wrong key, tampered ciphertext, or malformed input)');
  }

  return new TextDecoder().decode(ptBuf);
}

/** Custom error type for decryption failures. */
export class DecryptionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DecryptionError';
  }
}
